
import { supabase } from "@/integrations/supabase/client";

// Define an interface for the analytics data returned from Supabase
interface AdminAnalyticsData {
  userCount: number;
  clientCount: number;
  therapistCount: number;
  adminCount: number;
  unassignedCount: number;
  journalCount: number;
  averageJournalsPerClient: number;
  clientToTherapistRatio: number;
  pageHits: number;
  connectionRequests: number;
  countryDistribution: Array<{ country: string; count: number }>;
  languageDistribution: Array<{ language: string; count: number }>;
  therapistsWithConnections: Array<{ id: string; name: string; connectionCount: number }>;
  // Add new file metrics
  totalUploads: number;
  totalStorageMB: number;
  averageUploadsPerClient: number;
  averageStoragePerClientMB: number;
}

// Get analytics data for the admin dashboard
export async function getAnalyticsData() {
  try {
    console.log("Fetching admin analytics data...");
    
    // Call the security definer function that bypasses RLS restrictions
    const { data, error } = await supabase
      .rpc('get_admin_analytics');
      
    if (error) {
      console.error("Error fetching admin analytics:", error);
      return null;
    }
    
    // Type assertion to treat data as AdminAnalyticsData
    const analyticsData = data as unknown as AdminAnalyticsData;
    
    // Process language distribution to ensure "not specified" is properly labeled
    // AND combine primary and secondary languages
    if (analyticsData && analyticsData.languageDistribution) {
      // Create a map to combine counts for the same language
      const languageCountMap = new Map<string, number>();
      
      // Process each language entry
      analyticsData.languageDistribution.forEach(item => {
        // Check if this is from the secondary language field
        const isSecondary = item.language?.includes('language_secondary');
        
        // Extract just the language name and add "2nd" suffix if secondary
        let language = item.language || "Not Specified";
        if (isSecondary) {
          language = language.replace('language_secondary:', '') + " (2nd)";
        } else if (language.includes('language:')) {
          language = language.replace('language:', '');
        }
        
        // Clean up any whitespace from the extraction
        language = language.trim();
        
        const currentCount = languageCountMap.get(language) || 0;
        languageCountMap.set(language, currentCount + item.count);
      });
      
      // Convert the map back to array format
      analyticsData.languageDistribution = Array.from(languageCountMap).map(([language, count]) => ({
        language,
        count
      })).sort((a, b) => b.count - a.count); // Sort by count in descending order
    }
    
    // Ensure the data has consistent user counts
    if (analyticsData) {
      // Make sure userCount is at least equal to the sum of role counts
      const totalRoleUsers = (analyticsData.clientCount || 0) + 
                             (analyticsData.therapistCount || 0) + 
                             (analyticsData.adminCount || 0);
                           
      if (analyticsData.userCount < totalRoleUsers) {
        console.warn("Inconsistent user count detected. User count is less than sum of role counts.");
        analyticsData.userCount = totalRoleUsers;
      }
      
      // Default values for file metrics if not provided by the backend
      if (!analyticsData.totalUploads) analyticsData.totalUploads = 0;
      if (!analyticsData.totalStorageMB) analyticsData.totalStorageMB = 0;
      if (!analyticsData.averageUploadsPerClient) analyticsData.averageUploadsPerClient = 0;
      if (!analyticsData.averageStoragePerClientMB) analyticsData.averageStoragePerClientMB = 0;
      
      // Log the analytics data for debugging
      console.log("Admin analytics data processed:", {
        userCount: analyticsData.userCount,
        clientCount: analyticsData.clientCount,
        therapistCount: analyticsData.therapistCount,
        adminCount: analyticsData.adminCount,
        unassignedCount: analyticsData.unassignedCount,
        totalUploads: analyticsData.totalUploads,
        totalStorageMB: analyticsData.totalStorageMB
      });
    }
    
    return analyticsData;
  } catch (error) {
    console.error("Error in getAnalyticsData:", error);
    return null;
  }
}
